/**
 * Controlador principal para el módulo de reportes de errores de archivos de participantes.
 *
 * Este controlador maneja toda la lógica de negocio para la gestión de errores
 * de carga masiva de participantes, incluyendo listado, filtrado, exportación
 * y visualización de resúmenes de errores por archivo.
 *
 * @author Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 * @date [Fecha de creación: 2025-08-06]
 * @updated [Fecha de última modificación: 2025-08-11]
 * @updatedBy Victor Manuel Calle Londoño <victor.calle@iudigital.edu.co>
 */

import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Spinner from "components/MDSpinner";
import MDAlert from "components/Alert";
import FilterController from "components/filter/controller/FilterController";
import ParticipantFileReportsList from "../modules/ParticipantFileReportsList";
import { ParticipantFileReportsDetail } from "../modules/ParticipantFileReportsDetail";
import {
  ParticipantFileErrorsListService,
  ParticipantFileErrorsSummaryService,
  ParticipantFileErrorsExportService,
} from "../services/ParticipantFileReportsService";

export default function ParticipantFileReportsController() {
  const { id: activityId, id_file: uploadId } = useParams();
  const navigate = useNavigate();
  const filterControllerRef = useRef();

  // Configuración del filtro para errores
  const filterConfig = [
    { visibleName: "Número de fila", dbField: "row_number", type: "text" },
    { visibleName: "Tipo de error", dbField: "error_type", type: "select", source: "errorTypes" },
    { visibleName: "Severidad", dbField: "severity", type: "select", source: "severityTypes" },
    { visibleName: "Campo", dbField: "field_name", type: "text" },
    { visibleName: "Mensaje", dbField: "error_message", type: "text" },
    { visibleName: "Fecha del error", dbField: "created", type: "date" },
  ];

  const dataTable = "participant_upload_errors";

  // Variables de paginación
  const [totalPages, setTotalPages] = useState(0);
  const [totalData, setTotalData] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [entriesStart, setEntriesStart] = useState(0);
  const [entriesEnd, setEntriesEnd] = useState(0);

  // Variables de datos
  const [errorsList, setErrorsList] = useState([]);
  const [uploadInfo, setUploadInfo] = useState(null);
  const [errorSummary, setErrorSummary] = useState(null);
  const [criticalErrors, setCriticalErrors] = useState([]);

  // Variables de modal
  const [openDetail, setOpenDetail] = useState(false);
  const [selectedError, setSelectedError] = useState(null);

  // Controladores comunes
  const [loading, setLoading] = useState(false);
  const [typeAlert, setTypeAlert] = useState("");
  const [message, setMessage] = useState("");
  const [isAlert, setIsAlert] = useState(false);

  /**
   * Maneja las alertas del sistema
   * @param {string} typeAlert - Tipo de alerta (success, fail, etc.)
   * @param {string} message - Mensaje de la alerta
   */
  const handleAlert = (typeAlert, message) => {
    setTypeAlert(typeAlert);
    setMessage(message);
    setIsAlert(true);
  };

  /**
   * Carga el resumen de errores del archivo
   */
  const loadErrorSummary = async () => {
    try {
      setLoading(true);
      const result = await ParticipantFileErrorsSummaryService(uploadId);

      if (result.success) {
        setUploadInfo(result.payload.upload_info);
        setErrorSummary(result.payload.error_summary);
        setCriticalErrors(result.payload.critical_errors);
      } else {
        handleAlert("fail", result.message);
      }
    } catch (error) {
      handleAlert("fail", "Error al cargar el resumen de errores");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Maneja el cambio de página en la paginación
   * @param {Event} event - Evento de paginación
   * @param {number} value - Número de página seleccionada
   */
  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  /**
   * Ejecuta la búsqueda/filtrado del controlador de filtros
   */
  const callHandleClickSubmit = () => {
    if (filterControllerRef.current) {
      filterControllerRef.current.handleClickSubmit();
    }
  };

  /**
   * Maneja la apertura del modal de detalle de error
   * @param {Object} error - Datos del error seleccionado
   */
  const handleViewDetail = (error) => {
    setSelectedError(error);
    setOpenDetail(true);
  };

  /**
   * Cierra el modal de detalle
   */
  const closeDetail = () => {
    setOpenDetail(false);
    setSelectedError(null);
  };

  /**
   * Maneja la exportación de errores a Excel
   */
  const handleExportErrors = async () => {
    try {
      setLoading(true);
      const blob = await ParticipantFileErrorsExportService(uploadId);

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `errores_${uploadInfo?.original_filename || "archivo"}_${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      handleAlert("success", "Archivo de errores exportado exitosamente");
    } catch (error) {
      handleAlert("fail", "Error al exportar los errores");
    } finally {
      setLoading(false);
    }
  };

  /**
   * Maneja la navegación hacia atrás
   */
  const handleGoBack = () => {
    navigate(-1);
  };

  /**
   * Procesa los datos recibidos del FilterController
   * @param {Object} data - Datos del filtro
   */
  const processFilterData = (data) => {
    if (data) {
      setErrorsList(data || []);

      if (data.upload_info) {
        setUploadInfo(data.upload_info);
      }
    }
  };

  // Cargar resumen al montar el componente
  useEffect(() => {
    loadErrorSummary();
  }, [uploadId]);

  return (
    <>
      <Spinner loading={loading} />
      <MDAlert isAlert={isAlert} setIsAlert={setIsAlert} typeAlert={typeAlert} message={message} />
      <FilterController
        filterConfig={filterConfig}
        dataTable={dataTable}
        ref={filterControllerRef}
        setTotalData={setTotalData}
        setEntriesEnd={setEntriesEnd}
        setEntriesStart={setEntriesStart}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        setTotalPages={setTotalPages}
        setListData={processFilterData}
        handleAlert={handleAlert}
        upload_id={uploadId}
      />
      <ParticipantFileReportsList
        errorsList={errorsList}
        uploadInfo={uploadInfo}
        errorSummary={errorSummary}
        criticalErrors={criticalErrors}
        entriesStart={entriesStart}
        entriesEnd={entriesEnd}
        totalData={totalData}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        handleViewDetail={handleViewDetail}
        handleExportErrors={handleExportErrors}
        callHandleClickSubmit={callHandleClickSubmit}
        handleGoBack={handleGoBack}
      />
      <ParticipantFileReportsDetail
        open={openDetail}
        handleClose={closeDetail}
        errorData={selectedError}
      />
    </>
  );
}
